import React, { useState, useRef } from 'react';
import styled from 'styled-components';
import { message as antMessage, Avatar, Spin } from 'antd';
import { CameraIcon } from '@heroicons/react/outline';
import { colors, typography } from '../design-system';
import { userService } from '../services/userService';

interface ProfileImageUploadProps {
  currentImageUrl?: string;
  onImageUpdate?: (imageUrl: string) => void;
  size?: number;
  editable?: boolean;
}

const UploadContainer = styled.div<{ $size: number }>`
  position: relative;
  width: ${props => props.$size}px;
  height: ${props => props.$size}px;
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    opacity: 0.8;
  }
`;

const UploadOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${UploadContainer}:hover & {
    opacity: 1;
  }
`;

const UploadIcon = styled.div`
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  gap: 4px;
`;

const UploadText = styled.span`
  font-family: ${typography.fontFamilies.sans};
  font-size: ${typography.fontSizes.xxs};
  font-weight: ${typography.fontWeights.medium};
  color: white;
  text-align: center;
`;

const HiddenInput = styled.input`
  display: none;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 8px;
`;

export const ProfileImageUploadSimple: React.FC<ProfileImageUploadProps> = ({
  currentImageUrl,
  onImageUpdate,
  size = 64,
  editable = true
}) => {
  const [uploading, setUploading] = useState(false);
  const [imageUrl, setImageUrl] = useState(currentImageUrl);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      antMessage.error('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      antMessage.error('Image size must be less than 5MB');
      return;
    }

    setUploading(true);

    try {
      console.log('📸 [ProfileImageUpload] Compressing and converting image to base64');
      
      // Compress the image to fit within Cognito's 2048 character limit
      const compressedBase64 = await compressImageToBase64(file, 1500); // Target ~1500 chars to leave buffer
      
      console.log(`📏 [ProfileImageUpload] Compressed image size: ${compressedBase64.length} characters`);
      
      if (compressedBase64.length > 2000) {
        throw new Error('Image is too large even after compression. Please try a smaller image.');
      }

      // Update Cognito custom attribute with the compressed base64 data
      await userService.updateProfileImage(compressedBase64);
      console.log('✅ [ProfileImageUpload] Updated Cognito profile image');

      // Update local state
      setImageUrl(compressedBase64);
      
      // Notify parent component
      if (onImageUpdate) {
        onImageUpdate(compressedBase64);
      }

      antMessage.success('Profile image updated successfully');

    } catch (error) {
      console.error('❌ [ProfileImageUpload] Upload failed:', error);
      antMessage.error('Failed to upload image. Please try again.');
    } finally {
      setUploading(false);
      // Clear the input value so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const compressImageToBase64 = (file: File, maxChars: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        // Start with a small size and adjust as needed
        let size = 32; // Start very small for profile avatars
        
        // Try different sizes until we find one that fits within the character limit
        const trySize = (targetSize: number, quality: number = 0.7) => {
          canvas.width = targetSize;
          canvas.height = targetSize;
          
          // Draw the image resized to the canvas
          ctx?.drawImage(img, 0, 0, targetSize, targetSize);
          
          // Convert to base64 with compression
          const base64 = canvas.toDataURL('image/jpeg', quality);
          
          console.log(`🔄 [compressImageToBase64] Tried size ${targetSize}x${targetSize} with quality ${quality}: ${base64.length} chars`);
          
          return base64;
        };

        // Try progressively larger sizes until we hit the limit
        let result = trySize(size, 0.5); // Start with low quality
        
        // If still too big, try even smaller
        if (result.length > maxChars) {
          size = 24;
          result = trySize(size, 0.4);
        }
        
        // If still too big, try tiny size
        if (result.length > maxChars) {
          size = 16;
          result = trySize(size, 0.3);
        }
        
        // If still too big, try minimal size
        if (result.length > maxChars) {
          size = 12;
          result = trySize(size, 0.2);
        }

        if (result.length > maxChars) {
          reject(new Error(`Unable to compress image below ${maxChars} characters. Current size: ${result.length}`));
        } else {
          console.log(`✅ [compressImageToBase64] Final compressed image: ${size}x${size}, ${result.length} characters`);
          resolve(result);
        }
      };

      img.onerror = () => reject(new Error('Failed to load image'));
      
      // Create object URL from file to load into image
      img.src = URL.createObjectURL(file);
    });
  };

  const handleClick = () => {
    if (editable && !uploading) {
      fileInputRef.current?.click();
    }
  };

  return (
    <UploadContainer $size={size} onClick={handleClick}>
      <Avatar
        size={size}
        src={imageUrl}
        style={{
          backgroundColor: colors.background.secondary,
          color: colors.text.secondary,
          fontSize: size * 0.4,
          fontWeight: typography.fontWeights.medium
        }}
      >
        {!imageUrl && 'U'}
      </Avatar>

      {editable && (
        <UploadOverlay>
          <UploadIcon>
            <CameraIcon style={{ width: '16px', height: '16px' }} />
            <UploadText>
              {imageUrl ? 'Change' : 'Upload'}
            </UploadText>
          </UploadIcon>
        </UploadOverlay>
      )}

      {uploading && (
        <LoadingOverlay>
          <Spin size="small" />
        </LoadingOverlay>
      )}

      <HiddenInput
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
      />
    </UploadContainer>
  );
};
